/**
 * LoginPage cliente — email + senha (B1.5 spec §2.4).
 *
 * Backend: POST /api/v1/cliente/login. Mensagem genérica em falha (não
 * vaza se email existe vs senha errada). 401 = credenciais-invalidas.
 *
 * UX: form centralizado em card, sem nav (pre-login). Link pra
 * recuperar-senha embaixo. Mensagem de erro inline.
 *
 * Após login OK → redirect /cliente/dashboard.
 *
 * NÃO REVERTER:
 *   - Mensagem genérica em falha (NUNCA mostrar "email não existe")
 *   - Submit desabilitado durante loading (anti-double-submit)
 *   - Email lowercase + trim no submit (espelha backend)
 *   - Sem "lembrar senha" — cookie httpOnly já dura 7 dias
 */

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { Alert, Button, Input, Label } from '@/components/ui';
import { useClienteAuth } from '@/contexts/ClienteAuthContext';

export function LoginPage() {
  const { login } = useClienteAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErrorMsg(null);
    if (!email.trim() || !senha) {
      setErrorMsg('Preencha o e-mail (ou o usuário) e a senha.');
      return;
    }
    setLoading(true);
    try {
      await login(email.toLowerCase().trim(), senha);
      navigate('/cliente/dashboard');
    } catch {
      // Mensagem genérica — não revela se email existe ou senha errada
      setErrorMsg('E-mail, usuário ou senha não conferem. Confira e tente de novo.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="mx-auto max-w-md pt-8" data-testid="cliente-login-page">
      <div className="rounded-2xl border border-line bg-paper-surface p-6 shadow-card dark:border-line-dark dark:bg-paper-dark-surface md:p-8">
        <h1 className="mb-2 font-display text-3xl tracking-tight text-ink-1 dark:text-ink-dark-1">
          Entrar no Portal
        </h1>
        <p className="mb-6 text-sm text-ink-2 dark:text-ink-dark-2">
          Acompanhe chamados, orçamentos e manutenções do seu condomínio.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            {/* 26/10/2026: a portaria sem e-mail entra com o USUÁRIO que o síndico ou a IGM criou. */}
            <Label htmlFor="email" required>
              E-mail ou usuário
            </Label>
            <Input
              id="email"
              type="text"
              inputMode="email"
              autoCapitalize="none"
              autoComplete="username"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading}
              data-testid="login-email-input"
            />
          </div>
          <div>
            <Label htmlFor="senha" required>
              Senha
            </Label>
            <Input
              id="senha"
              type="password"
              autoComplete="current-password"
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
              required
              disabled={loading}
              data-testid="login-senha-input"
            />
          </div>

          {errorMsg && (
            <Alert variant="danger" data-testid="login-error">
              {errorMsg}
            </Alert>
          )}

          <Button
            type="submit"
            variant="primary"
            disabled={loading}
            className="w-full"
            data-testid="login-submit-btn"
          >
            {loading ? 'Entrando…' : 'Entrar'}
          </Button>

          <div className="pt-2 text-center text-sm">
            <Link
              to="/cliente/recuperar-senha"
              className="text-red-deep hover:underline dark:text-red-glow"
              data-testid="link-recuperar-senha"
            >
              Esqueci minha senha
            </Link>
          </div>
        </form>
      </div>

      <p className="mt-5 text-center text-sm text-ink-2 dark:text-ink-dark-2">
        Ainda não tem acesso? Peça à IGM ou ao síndico. Entra com usuário e esqueceu a senha? Quem criou o seu acesso
        define uma nova.
      </p>
    </div>
  );
}
